

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocale, useTranslations } from "next-intl";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import {
  resendCheckEmail,
  cancelCheckEmailFlow,
} from "@/lib/api/handlers/AuthenticationHandling";
import { getCookie } from "@/lib/auth/cookies";
import { throwServerError } from "@/lib/api/errors/ErrorHandler";

export function useCheckEmailForm(
  origin: "register" | "register-invite" | "reset-password",
  email: string,
  cooldownRemaining: number,
) {
  const [timer, setTimer] = useState(cooldownRemaining);
  const [isButtonDisabled, setIsButtonDisabled] = useState(cooldownRemaining > 0);
  const [isLoadingResend, setIsLoadingResend] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const locale = useLocale();
  const t = useTranslations("errors");

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (!isButtonDisabled || timer <= 0) return;

    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setIsButtonDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [isButtonDisabled]);

  async function handleResendClick() {
    setIsLoadingResend(true);
    setError("");
    try {
      const response = await resendCheckEmail(origin, email, locale);

      if (
        response &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        throwServerError(response);
      }

      setIsButtonDisabled(true);
      setTimer(99);
    } catch (error) {
      setError(translateBackendError(t, error instanceof Error ? error : "unknown"));
    } finally {
      setIsLoadingResend(false);
    }
  }

  async function handleBack() {
    window.dispatchEvent(new Event("navigationStart"));
    // Read the invite code BEFORE cleanup so invitees return to their invite form.
    let inviteCode: string | null = null;
    if (origin === "register-invite") {
      try {
        inviteCode = await getCookie("inviteCode");
      } catch {
        // ignore — falls back to the plain register route
      }
    }
    try {
      await cancelCheckEmailFlow();
    } catch {
      // Cookie cleanup failed — navigate anyway
    }
    if (origin === "reset-password") {
      navigate("/auth/reset-password");
    } else if (origin === "register-invite" && inviteCode) {
      navigate(`/auth/register?invite_code=${encodeURIComponent(inviteCode)}`);
    } else {
      navigate("/auth/register");
    }
  }

  return {
    timer,
    isButtonDisabled,
    isLoadingResend,
    error,
    handleResendClick,
    handleBack,
  };
}
